import React from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';

import ScoreBar from '../PerformanceReview4c/ScoreBar';
import Proctorship from '../PerformanceReview4c/Proctorship';
import InfoIconWithTooltip from 'components/InfoIconWithTooltip';

const useStyles = makeStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(4.5),
    width: '100%'
  },
  scoreComponent: {
    display: 'flex',
    justifyContent: "space-between",
    alignItems: 'flex-start',
    width: '100%'
  },
  scoreSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start'
  },
  scoreHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    marginBottom: theme.spacing(2)
  }
}));

const getFeedbackByScore = (score, maxScore = 10) => {
  switch (true) {
    case (score >= 8):
      return "Excellent performance! Your case interview skills demonstrate strong analytical thinking and clear communication.";
    case (score >= 6):
      return "Good work! Continue refining your framework development and data analysis skills to reach the next level.";
    case (score >= 4):
      return "You're making progress! Focus on structuring your approach and improving hypothesis testing.";
    case (score >= 2):
      return "Keep practicing! Work on case introduction and developing logical frameworks.";
    default:
      return "Let's start with the fundamentals — focus on understanding case interview basics first";
  }
};

const CaseMasterScore = ({ 
  score, 
  proctorRecords = [],
  maxScore = 10, 
  scoreLabel = "My score:", 
  tooltipTitle = "Score based on case interview performance across multiple dimensions.",
  showDescription = false
}) => {
  const classes = useStyles();
  const theme = useTheme();

  const getCatIndex = React.useCallback((score) => {
    // For case-master (1-10 scale), map to cat GIFs 1-5
    const clamped = Math.min(Math.max(score, 1), 10);
    // Map 1-10 scale to 1-5 scale for cat GIFs
    const mappedScore = Math.ceil((clamped / 10) * 5);
    return Math.min(Math.max(mappedScore, 1), 5);
  }, []);

  return (
    <Box className={classes.container}>
      <Box className={classes.scoreComponent}>
        <Box className={classes.scoreSection}>
          <Box className={classes.scoreHeader}>
            <Typography variant='h4-medium'>{scoreLabel} </Typography>
            <Typography variant='h4-bold' color='primary.clr-400'>{Math.min(Math.max(score, 1), 10).toFixed(2)}</Typography>
            <img
              src={`https://assets.languify.in/gifs/cat-${getCatIndex(score)}.gif`}
              alt='cat-gif'
              width={36}
              style={{ marginLeft: '8px' }}
            />
            <InfoIconWithTooltip
              title={tooltipTitle}
              sx={{ fontSize: '35px', marginTop: '2px', marginLeft: '16.5px' }} 
              placement='top'
            />
          </Box>
          <ScoreBar scale={Math.min(Math.max(score, 1), 10)} maxScale={maxScore} />
        </Box>

        <Proctorship records={proctorRecords} />
      </Box>

      {showDescription && (
        <Box sx={{ padding: theme.spacing(2, 0) }}>
          <Typography
            variant='h6-medium'
            color='neutral.clr-600'
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {getFeedbackByScore(parseInt(Math.min(Math.max(score, 1), 10)), maxScore)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default CaseMasterScore;
